import { c as _c } from "react/compiler-runtime";
import { useAnalytics } from '@repo/analytics/analytics-provider';
import { Icon } from '@repo/design-system/components/ds/icon';
import { Logo } from '@repo/design-system/components/ds/logo';
import { Type } from '@repo/design-system/components/ui/type';
import type { MaterialSymbolName } from '@repo/design-system/types/material-symbol-names';
import { useHostType } from '@repo/host/hooks/use-host-info';
import { useT } from '@repo/i18n/components/i18n.client';
import { css } from '@repo/panda/css';
import { button } from '@repo/panda/recipes';
import { useAppSelector } from '@repo/store/maps/hooks';
import { selectExternalMapId, selectMapSupportsLiveLocation, selectMapSupportsTeleport, selectTitleId, selectTitleName } from '@repo/store/maps/slices/mapData';
import type { MapDownloadContent, MapValuePropId } from '@repo/types/analytic-events/maps-analytic-events';
import { Link } from '@repo/ui/components/link/link';
import { OpenInWandBar } from '@repo/ui/components/open-in-wand-bar/open-in-wand-bar';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMapCompassEnabled } from '../compass/use-map-compass-enabled';
import { useMapDownloadPath } from '../utils/use-map-download-path';
import { getApplicableMapValueProps, shuffleMapValueProps } from './value-props';
const ROTATION_INTERVAL_MS = 4500;
type ValuePropContent = {
  icon: MaterialSymbolName | 'wand_logo';
  subtitle: string;
  title: string;
};
export function ValuePropDownloadBar() {
  const $ = _c(145);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["game_map"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useT(t0);
  const analytics = useAnalytics();
  const platform = useHostType();
  const externalMapId = useAppSelector(selectExternalMapId);
  const titleId = useAppSelector(selectTitleId);
  const titleName = useAppSelector(selectTitleName);
  const supportsTeleport = useAppSelector(selectMapSupportsTeleport);
  const supportsLiveLocation = useAppSelector(selectMapSupportsLiveLocation);
  const mapCompassEnabled = useMapCompassEnabled();
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const t1 = supportsLiveLocation && mapCompassEnabled;
  let t2;
  if ($[1] !== supportsLiveLocation || $[2] !== supportsTeleport || $[3] !== t1) {
    t2 = getApplicableMapValueProps({
      supportsCompass: t1,
      supportsLiveLocation,
      supportsTeleport
    });
    $[1] = supportsLiveLocation;
    $[2] = supportsTeleport;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const applicableValueProps = t2;
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const [rotationOrder, setRotationOrder] = useState(t3);
  const [rotationMapId, setRotationMapId] = useState(null);
  const lastImpressionRef = useRef(null);
  let t4;
  let t5;
  if ($[6] !== applicableValueProps || $[7] !== externalMapId) {
    t4 = () => {
      if (!externalMapId) {
        return;
      }
      setActiveIndex(0);
      setRotationOrder(shuffleMapValueProps(applicableValueProps));
      setRotationMapId(externalMapId);
    };
    t5 = [applicableValueProps, externalMapId];
    $[6] = applicableValueProps;
    $[7] = externalMapId;
    $[8] = t4;
    $[9] = t5;
  } else {
    t4 = $[8];
    t5 = $[9];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[10] !== isPaused || $[11] !== rotationOrder.length) {
    t6 = () => {
      if (isPaused || rotationOrder.length < 2) {
        return;
      }
      const timer = setInterval(() => {
        setActiveIndex(index => (index + 1) % rotationOrder.length);
      }, ROTATION_INTERVAL_MS);
      return () => clearInterval(timer);
    };
    t7 = [isPaused, rotationOrder.length];
    $[10] = isPaused;
    $[11] = rotationOrder.length;
    $[12] = t6;
    $[13] = t7;
  } else {
    t6 = $[12];
    t7 = $[13];
  }
  useEffect(t6, t7);
  let t8;
  if ($[14] !== t) {
    t8 = t(_temp);
    $[14] = t;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t) {
    t9 = t(_temp2);
    $[16] = t;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t8 || $[19] !== t9) {
    t10 = {
      icon: "star",
      title: t8,
      subtitle: t9
    };
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t) {
    t11 = t(_temp3);
    $[21] = t;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t) {
    t12 = t(_temp4);
    $[23] = t;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t11 || $[26] !== t12) {
    t13 = {
      icon: "check_circle",
      title: t11,
      subtitle: t12
    };
    $[25] = t11;
    $[26] = t12;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t) {
    t14 = t(_temp5);
    $[28] = t;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== t) {
    t15 = t(_temp6);
    $[30] = t;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== t14 || $[33] !== t15) {
    t16 = {
      icon: "wand_logo",
      title: t14,
      subtitle: t15
    };
    $[32] = t14;
    $[33] = t15;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t) {
    t17 = t(_temp7);
    $[35] = t;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== t) {
    t18 = t(_temp8);
    $[37] = t;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== t17 || $[40] !== t18) {
    t19 = {
      icon: "block",
      title: t17,
      subtitle: t18
    };
    $[39] = t17;
    $[40] = t18;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== t) {
    t20 = t(_temp9);
    $[42] = t;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== t) {
    t21 = t(_temp10);
    $[44] = t;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== t20 || $[47] !== t21) {
    t22 = {
      icon: "wand_logo",
      title: t20,
      subtitle: t21
    };
    $[46] = t20;
    $[47] = t21;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] !== t) {
    t23 = t(_temp11);
    $[49] = t;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== t) {
    t24 = t(_temp12);
    $[51] = t;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] !== t23 || $[54] !== t24) {
    t25 = {
      icon: "sprint",
      title: t23,
      subtitle: t24
    };
    $[53] = t23;
    $[54] = t24;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] !== t) {
    t26 = t(_temp13);
    $[56] = t;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  let t27;
  if ($[58] !== t) {
    t27 = t(_temp14);
    $[58] = t;
    $[59] = t27;
  } else {
    t27 = $[59];
  }
  let t28;
  if ($[60] !== t26 || $[61] !== t27) {
    t28 = {
      icon: "near_me",
      title: t26,
      subtitle: t27
    };
    $[60] = t26;
    $[61] = t27;
    $[62] = t28;
  } else {
    t28 = $[62];
  }
  let t29;
  if ($[63] !== t) {
    t29 = t(_temp15);
    $[63] = t;
    $[64] = t29;
  } else {
    t29 = $[64];
  }
  let t30;
  if ($[65] !== t) {
    t30 = t(_temp16);
    $[65] = t;
    $[66] = t30;
  } else {
    t30 = $[66];
  }
  let t31;
  if ($[67] !== t29 || $[68] !== t30) {
    t31 = {
      icon: "visibility_off",
      title: t29,
      subtitle: t30
    };
    $[67] = t29;
    $[68] = t30;
    $[69] = t31;
  } else {
    t31 = $[69];
  }
  let t32;
  if ($[70] !== t) {
    t32 = t(_temp17);
    $[70] = t;
    $[71] = t32;
  } else {
    t32 = $[71];
  }
  let t33;
  if ($[72] !== t) {
    t33 = t(_temp18);
    $[72] = t;
    $[73] = t33;
  } else {
    t33 = $[73];
  }
  let t34;
  if ($[74] !== t32 || $[75] !== t33) {
    t34 = {
      icon: "explore",
      title: t32,
      subtitle: t33
    };
    $[74] = t32;
    $[75] = t33;
    $[76] = t34;
  } else {
    t34 = $[76];
  }
  let t35;
  if ($[77] !== t10 || $[78] !== t13 || $[79] !== t16 || $[80] !== t19 || $[81] !== t22 || $[82] !== t25 || $[83] !== t28 || $[84] !== t31 || $[85] !== t34) {
    t35 = {
      join_players: t10,
      progress: t13,
      overlay: t16,
      no_ads: t19,
      all_in_one: t22,
      teleport: t25,
      live_location: t28,
      fog_of_war: t31,
      compass: t34
    };
    $[77] = t10;
    $[78] = t13;
    $[79] = t16;
    $[80] = t19;
    $[81] = t22;
    $[82] = t25;
    $[83] = t28;
    $[84] = t31;
    $[85] = t34;
    $[86] = t35;
  } else {
    t35 = $[86];
  }
  const content = t35;
  const isRotationReady = rotationMapId === externalMapId;
  const activeValueProp = (isRotationReady ? rotationOrder[activeIndex % rotationOrder.length] : undefined) ?? "join_players";
  const utmContent = `map_value_prop_${activeValueProp}`;
  let t36;
  if ($[87] !== externalMapId || $[88] !== titleId) {
    t36 = {
      mapId: externalMapId,
      titleId
    };
    $[87] = externalMapId;
    $[88] = titleId;
    $[89] = t36;
  } else {
    t36 = $[89];
  }
  const downloadPath = useMapDownloadPath("map_value_prop_bar", t36, "/download", utmContent);
  let t37;
  let t38;
  if ($[90] !== activeIndex || $[91] !== activeValueProp || $[92] !== analytics || $[93] !== externalMapId || $[94] !== isRotationReady || $[95] !== platform || $[96] !== rotationOrder.length || $[97] !== titleId || $[98] !== titleName) {
    t37 = () => {
      if (!externalMapId || !isRotationReady || rotationOrder.length === 0) {
        return;
      }
      const impressionKey = `${externalMapId}:${activeIndex}:${activeValueProp}`;
      if (lastImpressionRef.current === impressionKey) {
        return;
      }
      lastImpressionRef.current = impressionKey;
      analytics.event("game_map_value_prop_bar_impression", {
        mapId: externalMapId,
        platform,
        titleId: titleId ?? "",
        titleName: titleName ?? "",
        valueProp: activeValueProp
      });
    };
    t38 = [activeIndex, activeValueProp, analytics, externalMapId, isRotationReady, platform, rotationOrder.length, titleId, titleName];
    $[90] = activeIndex;
    $[91] = activeValueProp;
    $[92] = analytics;
    $[93] = externalMapId;
    $[94] = isRotationReady;
    $[95] = platform;
    $[96] = rotationOrder.length;
    $[97] = titleId;
    $[98] = titleName;
    $[99] = t37;
    $[100] = t38;
  } else {
    t37 = $[99];
    t38 = $[100];
  }
  useEffect(t37, t38);
  if (!externalMapId || !isRotationReady || rotationOrder.length === 0) {
    return null;
  }
  const t39 = titleId ?? "";
  const t40 = titleName ?? "";
  let t41;
  if ($[101] !== activeValueProp || $[102] !== externalMapId || $[103] !== platform || $[104] !== t39 || $[105] !== t40) {
    t41 = {
      mapId: externalMapId,
      platform,
      titleId: t39,
      titleName: t40,
      valueProp: activeValueProp
    };
    $[101] = activeValueProp;
    $[102] = externalMapId;
    $[103] = platform;
    $[104] = t39;
    $[105] = t40;
    $[106] = t41;
  } else {
    t41 = $[106];
  }
  const analyticsParams = t41;
  let t42;
  if ($[107] !== t) {
    t42 = t(_temp19);
    $[107] = t;
    $[108] = t42;
  } else {
    t42 = $[108];
  }
  let t43;
  let t44;
  if ($[109] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = css.raw({
      background: "blackAlpha.40",
      boxShadow: "[none]",
      gap: "0",
      padding: "16",
      width: "[520px]"
    });
    t44 = {
      size: "sm"
    };
    $[109] = t43;
    $[110] = t44;
  } else {
    t43 = $[109];
    t44 = $[110];
  }
  let t45;
  if ($[111] !== analytics || $[112] !== analyticsParams) {
    t45 = () => analytics.event("game_map_value_prop_bar_dismiss", analyticsParams);
    $[111] = analytics;
    $[112] = analyticsParams;
    $[113] = t45;
  } else {
    t45 = $[113];
  }
  let t46;
  let t47;
  let t48;
  if ($[114] === Symbol.for("react.memo_cache_sentinel")) {
    t46 = () => setIsPaused(true);
    t47 = () => setIsPaused(false);
    t48 = css({
      flex: "[1 1 0]",
      height: "[36px]",
      minWidth: "0",
      position: "relative"
    });
    $[114] = t46;
    $[115] = t47;
    $[116] = t48;
  } else {
    t46 = $[114];
    t47 = $[115];
    t48 = $[116];
  }
  let t49;
  if ($[117] !== activeValueProp || $[118] !== content || $[119] !== rotationOrder) {
    let t50;
    if ($[121] !== activeValueProp || $[122] !== content) {
      t50 = valueProp => {
        const item = content[valueProp];
        const isActive = valueProp === activeValueProp;
        return <div key={valueProp} aria-hidden={!isActive} className={css({
          alignItems: "center",
          display: "flex",
          gap: "8",
          inset: "0",
          opacity: isActive ? 1 : 0,
          position: "absolute",
          transition: "[opacity 150ms cubic-bezier(0.33, 0, 0.2, 1)]"
        })}><span className={css({
            alignItems: "center",
            display: "flex",
            flexShrink: 0,
            height: "[20px]",
            justifyContent: "center",
            width: "[20px]"
          })}>{item.icon === "wand_logo" ? <Logo variant="icon" aria-hidden={true} color="green.500" width="[16px]" height="[12px]" /> : <Icon icon={item.icon} size="md" decorative={true} className={css({
              color: "green.500"
            })} />}</span><span className={css({
            display: "flex",
            flexDirection: "column",
            minWidth: "0"
          })}><Type as="span" variant="header-xs" color="white" truncate={true}>{item.title}</Type><Type as="span" variant="body-xs" color="white" truncate={true}>{item.subtitle}</Type></span></div>;
      };
      $[121] = activeValueProp;
      $[122] = content;
      $[123] = t50;
    } else {
      t50 = $[123];
    }
    t49 = rotationOrder.map(t50);
    $[117] = activeValueProp;
    $[118] = content;
    $[119] = rotationOrder;
    $[120] = t49;
  } else {
    t49 = $[120];
  }
  let t50;
  if ($[124] !== t48 || $[125] !== t49) {
    t50 = <div className={t48}>{t49}</div>;
    $[124] = t48;
    $[125] = t49;
    $[126] = t50;
  } else {
    t50 = $[126];
  }
  let t51;
  if ($[127] !== analytics || $[128] !== analyticsParams) {
    t51 = () => analytics.event("game_map_value_prop_bar_download_click", analyticsParams);
    $[127] = analytics;
    $[128] = analyticsParams;
    $[129] = t51;
  } else {
    t51 = $[129];
  }
  let t52;
  if ($[130] === Symbol.for("react.memo_cache_sentinel")) {
    t52 = button({
      color: "brand",
      size: "sm"
    });
    $[130] = t52;
  } else {
    t52 = $[130];
  }
  let t53;
  if ($[131] !== t) {
    t53 = t(_temp20);
    $[131] = t;
    $[132] = t53;
  } else {
    t53 = $[132];
  }
  let t54;
  if ($[133] !== downloadPath || $[134] !== t51 || $[135] !== t52 || $[136] !== t53) {
    t54 = <Link href={downloadPath} onClick={t51} className={t52}>{t53}</Link>;
    $[133] = downloadPath;
    $[134] = t51;
    $[135] = t52;
    $[136] = t53;
    $[137] = t54;
  } else {
    t54 = $[137];
  }
  let t55;
  if ($[138] !== externalMapId || $[139] !== t42 || $[140] !== t43 || $[141] !== t45 || $[142] !== t50 || $[143] !== t54) {
    t55 = <OpenInWandBar position="absolute" dismissKey={externalMapId} dismissLabel={t42} actionGap="6" className={t43} dismissButtonProps={t44} onDismiss={t45} onMouseEnter={t46} onMouseLeave={t47} content={t50} actionButton={t54} />;
    $[138] = externalMapId;
    $[139] = t42;
    $[140] = t43;
    $[141] = t45;
    $[142] = t50;
    $[143] = t54;
    $[144] = t55;
  } else {
    t55 = $[144];
  }
  return t55;
}
function _temp20($_18) {
  return $_18.value_prop_bar.get_wand_free;
}
function _temp19($_17) {
  return $_17.dismiss;
}
function _temp18($_16) {
  return $_16.value_prop_bar.compass.subtitle;
}
function _temp17($_15) {
  return $_15.value_prop_bar.compass.title;
}
function _temp16($_14) {
  return $_14.value_prop_bar.fog_of_war.subtitle;
}
function _temp15($_13) {
  return $_13.value_prop_bar.fog_of_war.title;
}
function _temp14($_12) {
  return $_12.value_prop_bar.live_location.subtitle;
}
function _temp13($_11) {
  return $_11.value_prop_bar.live_location.title;
}
function _temp12($_10) {
  return $_10.value_prop_bar.teleport.subtitle;
}
function _temp11($_9) {
  return $_9.value_prop_bar.teleport.title;
}
function _temp10($_8) {
  return $_8.value_prop_bar.all_in_one.subtitle;
}
function _temp9($_7) {
  return $_7.value_prop_bar.all_in_one.title;
}
function _temp8($_6) {
  return $_6.value_prop_bar.no_ads.subtitle;
}
function _temp7($_5) {
  return $_5.value_prop_bar.no_ads.title;
}
function _temp6($_4) {
  return $_4.value_prop_bar.overlay.subtitle;
}
function _temp5($_3) {
  return $_3.value_prop_bar.overlay.title;
}
function _temp4($_2) {
  return $_2.value_prop_bar.progress.subtitle;
}
function _temp3($_1) {
  return $_1.value_prop_bar.progress.title;
}
function _temp2($_0) {
  return $_0.value_prop_bar.join_players.subtitle;
}
function _temp($) {
  return $.value_prop_bar.join_players.title;
}